import React, { useEffect, useState } from 'react';
import './ThemeToggle.css';

/**
 * Resolves the theme the same way the inline bootstrap in public/index.html
 * does. The two MUST agree: the bootstrap runs before first paint to avoid a
 * flash of the wrong theme, and if React then resolved it differently the page
 * would visibly swap after hydration. A third copy is baked into every blog
 * post by scripts/theme-blog.js and has to match as well, or moving between the
 * app and the blog would change theme.
 *
 * An explicit choice always wins. Otherwise: light.
 *
 * Light became the first-run default on 2026-09-17, to test whether it converts
 * better than dark. The OS prefers-color-scheme setting is deliberately NOT
 * consulted any more — honouring it would route every dark-at-the-OS visitor to
 * dark, and the comparison would measure audience mix rather than the theme.
 *
 * Worth knowing when reading conversion numbers: the effect below persists the
 * resolved theme on MOUNT, not only on toggle. So everyone who has already
 * loaded the site carries a stored value and is unaffected by this change —
 * the new default reaches genuinely new visitors only. That also means
 * reverting the default will not pull back anyone who has since been pinned to
 * light.
 */
export function resolveTheme(): 'light' | 'dark' {
  try {
    const saved = localStorage.getItem('theme');
    if (saved === 'light' || saved === 'dark') return saved;
  } catch {
    // Storage unavailable (private mode) — treat as a first-time visitor.
  }
  return 'light';
}

const ThemeToggle: React.FC = () => {
  const [theme, setTheme] = useState<'light' | 'dark'>(resolveTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);

    // Mobile browser chrome. Without this the address bar keeps the near-black
    // it was born with and frames a cream page, which reads as a rendering bug
    // on a phone. Values are --ground for each theme.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#1a1412' : '#faf6ee');

    try {
      localStorage.setItem('theme', theme);
    } catch {
      // Private mode: the toggle still works for this session.
    }
  }, [theme]);

  const toggle = () => setTheme(t => t === 'dark' ? 'light' : 'dark');

  return (
    <button
      className="theme-toggle-btn"
      onClick={toggle}
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
    >
      {theme === 'dark' ? (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="5"/>
          <line x1="12" y1="1" x2="12" y2="3"/>
          <line x1="12" y1="21" x2="12" y2="23"/>
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
          <line x1="1" y1="12" x2="3" y2="12"/>
          <line x1="21" y1="12" x2="23" y2="12"/>
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
        </svg>
      ) : (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
        </svg>
      )}
    </button>
  );
};

export default ThemeToggle;
